import type { LinkOptions } from "@tanstack/react-router";

/** Company and location details shared by the website and the print files. */
export const COMPANY = {
  legalName: "TOMA 1807 GmbH & Co KG",
  name: "Alte Papierfabrik",
  street: "Friedrich-Ebert-Str. 130",
  city: "42117 Wuppertal",
  phone: "0202 / 317 99 95",
  phoneHref: "tel:+492023179995",
  email: "buchen@altepapierfabrik.eu",
  website: "altepapierfabrik.eu",
  instagram: "@altepapierfabrik_wuppertal",
} as const;

export type Activity__NEW__ = {
  key: string;
  name: string;
  description: string;
  shortDescription: string;
  link: LinkOptions;
  bookingLink?: LinkOptions & { label: string };
  highlights: {
    key: string;
    label: string;
  }[];
  brand: {
    logo: string;
    backgroundImage: string;
    primaryColor: string;
    primaryForegroundColor: string;
  };
};

export const ACTIVITIES__NEW__ = {
  aposto: {
    key: "aposto",
    name: "Aposto",
    shortDescription: "Pizza, Pasta & mehr",
    description: "",
    link: {
      href: "https://www.aposto.eu/restaurants/wuppertal-papierfabrik/",
    },
    bookingLink: {
      label: "Tisch reservieren",
      href: "https://www.aposto.eu/restaurants/wuppertal-papierfabrik/",
    },
    brand: {
      logo: "/images/aposto_logo.png",
      backgroundImage: "/images/aposto_1.png",
      primaryColor: "#ffffff",
      primaryForegroundColor: "#ffffff",
    },
    highlights: [],
  },
  bowling: {
    key: "bowling",
    name: "Bowling",
    shortDescription: "Bowling neu erleben",
    description: "12 Bahnen & riesige LED Wand",
    link: {
      to: "/angebote/bowling",
    },
    bookingLink: {
      to: "/angebote/bowling",
      hash: "buchen",
      label: "Jetzt buchen",
    },
    brand: {
      logo: "/images/bowling_logo.png",
      backgroundImage: "/images/bowling_1.png",
      primaryColor: "oklch(0.93 0.1005 198.46)",
      primaryForegroundColor: "#000000",
    },
    highlights: [
      {
        key: "12-bahnen",
        label: "12 moderne Bahnen",
      },
      {
        key: "led-wand",
        label: "Riesige LED Wand",
      },
      {
        key: "minigames",
        label: "Minigames",
      },
    ],
  },
  minigolf: {
    key: "minigolf",
    name: "Minigolf",
    shortDescription: "22 Bahnen 3D Indoorspaß",
    description: "22 Bahnen 3D Schwarzlicht",
    link: {
      to: "/angebote/3d-schwarzlicht-minigolf",
    },
    bookingLink: {
      to: "/angebote/3d-schwarzlicht-minigolf",
      hash: "buchen",
      label: "Jetzt buchen",
    },
    brand: {
      logo: "/images/minigolf_logo.png",
      backgroundImage: "/images/minigolf_1.png",
      primaryColor: "oklch(0.94 0.200572 105.7727)",
      primaryForegroundColor: "#000000",
    },
    highlights: [
      {
        key: "22-loch",
        label: "22 Bahnen",
      },
      {
        key: "3d-schwarzlicht",
        label: "3D Schwarzlicht",
      },
      {
        key: "indoorspaß",
        label: "750 qm Indoorspaß",
      },
    ],
  },
  event: {
    key: "event",
    name: "Event",
    shortDescription: "Für jede Veranstaltung",
    description: "Und dabei noch Cocktails!",
    link: { href: "https://www.toma-events.de/" },
    brand: {
      logo: "/images/event_logo.png",
      backgroundImage: "/images/event_1.png",
      primaryColor: "oklch(1 0 0)",
      primaryForegroundColor: "#ffffff",
    },
    highlights: [],
  },
} as const satisfies Record<string, Activity__NEW__>;

/**
 * @deprecated
 */
export type Activity =
  | {
      key: string;
      label: string;
      description: string;
      shortDescription: string;
      href: string;
      target?: string;
      to?: never;
      logo: string;
      backgroundImage: string;
      primaryColor: string;
      highlights: {
        key: string;
        label: string;
      }[];
      bookingLink: { href: string };
    }
  | {
      key: string;
      label: string;
      description: string;
      shortDescription: string;
      href?: never;
      target?: never;
      to: LinkOptions["to"];
      logo: string;
      backgroundImage: string;
      primaryColor: string;
      highlights: {
        key: string;
        label: string;
      }[];
      bookingLink: LinkOptions;
    };

/**
 * @deprecated
 */
export const ACTIVITIES = {
  enchilada: {
    key: "enchilada",
    label: "Enchilada",
    href: "https://wuppertal.enchilada.de/",
    target: "_blank",
    logo: "/images/enchilada_logo.png",
    backgroundImage: "/images/enchilada_1.png",
    shortDescription: "Pizza, Pasta & mehr",
    description: "Leckere authentische Küche",
    primaryColor: "oklch(1 0 0)",
    bookingLink: {
      href: "",
    },
  } as Activity,
  bowling: {
    key: "bowling",
    label: "Bowling",
    to: "/angebote/bowling",
    logo: "/images/bowling_logo.png",
    backgroundImage: "/images/bowling_1.png",
    shortDescription: "Bowling neu erleben",
    description: "12 Bahnen & riesige LED Wand",
    primaryColor: "oklch(0.93 0.1005 198.46)",
    highlights: [],
    bookingLink: {
      to: "/angebote/bowling",
    },
  } as Activity,
  "3d-schwarzlicht-minigolf": {
    key: "3d-schwarzlicht-minigolf",
    label: "Minigolf",
    to: "/angebote/3d-schwarzlicht-minigolf",
    logo: "/images/minigolf_logo.png",
    backgroundImage: "/images/minigolf_1.png",
    shortDescription: "22 Loch 3D Indoorspaß",
    description: "22 Loch 3D Schwarzlicht",
    primaryColor: "oklch(0.94 0.200572 105.7727)",
    highlights: [
      {
        key: "22-loch",
        label: "22 Loch",
      },
      {
        key: "3d-schwarzlicht",
        label: "3D Schwarzlicht",
      },
      {
        key: "indoorspaß",
        label: "750 qm Indoorspaß",
      },
    ],
    bookingLink: {
      to: "/angebote/3d-schwarzlicht-minigolf",
      hash: "buchen",
    },
  } as Activity,
  event: {
    key: "event",
    label: "Event",
    href: "https://www.toma-events.de/",
    target: "_blank",
    logo: "/images/event_logo.png",
    backgroundImage: "/images/event_1.png",
    shortDescription: "Für jede Veranstaltung",
    description: "Und dabei noch Cocktails!",
    primaryColor: "oklch(1 0 0)",
  } as Activity,
} as const;

export type NavItem__NEW__ = {
  key: string;
  label: string;
  link: LinkOptions;
  items?: NavItem__NEW__[];
};

export const NAV_ITEMS__NEW__: NavItem__NEW__[] = [
  {
    key: "news",
    label: "News & Events",
    link: {
      to: "/",
      hash: "events",
    },
  },
  {
    key: "gruppenbuchung",
    label: "Gruppenbuchung",
    link: {
      to: "/",
      hash: "gruppenbuchung",
    },
  },
  // {
  //   key: "Gutscheine",
  //   label: "Gutscheine",
  //   link: {
  //     to: "/",
  //   },
  // },
  // {
  //   key: "Aktionen",
  //   label: "Aktionen",
  //   link: {
  //     to: "/",
  //   },
  // },
  // {
  //   key: "Support",
  //   label: "Support",
  //   link: {
  //     to: "/",
  //   },
  // },
  // {
  //   key: "FAQ's",
  //   label: "FAQ's",
  //   link: {
  //     to: "/",
  //   },
  // },
];

/**
 * @deprecated
 */
type NavItem =
  | {
      key: string;
      label: string;
      description?: string;
      href: string;
      target?: string;
      to?: never;
      items?: NavItem[];
      backgroundImage?: string;
    }
  | {
      key: string;
      label: string;
      description?: string;
      href?: never;
      target?: never;
      to: LinkOptions["to"];
      items?: NavItem[];
      backgroundImage?: string;
    };

/**
 * @deprecated
 */
export const NAV_ITEMS: NavItem[] = [
  {
    key: "Gutscheine",
    label: "Gutscheine",
    to: "/",
  },
  {
    key: "Aktionen",
    label: "Aktionen",
    to: "/",
  },
  {
    key: "Support",
    label: "Support",
    to: "/",
  },
  {
    key: "FAQ's",
    label: "FAQ's",
    to: "/",
  },
];

type OpeningHours = {
  key: string;
  label: string;
  hours: string;
};

export const OPENING_HOURS: OpeningHours[] = [
  {
    key: "Mo",
    label: "Montag",
    hours: "12:00–22:00",
  },
  {
    key: "Di",
    label: "Dienstag",
    hours: "12:00–22:00",
  },
  {
    key: "Mi",
    label: "Mittwoch",
    hours: "12:00–22:00",
  },
  {
    key: "Do",
    label: "Donnerstag",
    hours: "12:00–22:00",
  },
  {
    key: "Fr",
    label: "Freitag",
    hours: "12:00–00:00",
  },
  {
    key: "Sa",
    label: "Samstag",
    hours: "12:00–00:00",
  },
  {
    key: "So",
    label: "Sonntag",
    hours: "11:00–22:00",
  },
];
